import * as vp from 'vopay';
import { Box, toBool, withStyles, Card, BodyText } from 'vopay';
import { FunctionComponent, isValidElement, useEffect, useMemo, useState } from 'react';
import { useClientData } from '@/providers/ClientData.provider';
import { useToast } from '@/providers/Toast.provider';
import { useAlertDialog } from '@/providers/AlertDialog.provider';
import { useDataFetchIndicator } from '@/hooks';
import { Checkbox } from 'UI';
import Toggle from '@/components/UI/Toggle/Toggle';
import useELinxSettings from '@/hooks/useApi/useELinxSettings';
import useFetchSubAccounts from '@/hooks/useApi/useFetchSubAccounts';
import styles from './ElinxSettingsForm.module.scss';
import isEqual from 'lodash/isEqual';
import {
  buildGeneralSettings,
  buildCardPayments,
  buildSenderNotifications,
  buildRecipientNotifications,
  buildCommunications,
} from './buildElinxSettingsRows.helper';
import { useForm, TemplateData, RequiredIcon } from '@/components/Form/utils';
import { FieldData } from '@/components/Form/types';
import { Button } from 'UI';
import { useTranslation } from 'react-i18next';

const classes = withStyles(styles);

type ElinxSettingsProps = {
  onSubmit?: () => Promise<void>;
  onClose: () => void;
};

export type ElinxSettingsInfo = {
  TokenExpirationTime: number;
  ReceiverNotifications: boolean;
  SenderNotifications: boolean;
  SenderEmailAddress: string;
  IsDeclineEnabled: boolean;
  ELinxReminderEnabled: boolean;
  ELinxReminderFrequency: number;
  DefaultNotificationMethod: vp.ElinxNotificationMethod;
  Trigger3DS: boolean;
  RejectDuplicateRequests: boolean;
};

const ElinxSettingsForm: FunctionComponent<ElinxSettingsProps> = ({
  onClose,
  onSubmit = () => Promise.resolve(null),
}) => {
  const [defaultValues, setDefaultValues] = useState<ElinxSettingsInfo>({
    TokenExpirationTime: 0,
    IsDeclineEnabled: false,
    SenderEmailAddress: '',
    SenderNotifications: false,
    ReceiverNotifications: false,
    ELinxReminderEnabled: false,
    ELinxReminderFrequency: 7, // default is 7 days
    DefaultNotificationMethod: 'email',
    Trigger3DS: false,
    RejectDuplicateRequests: false,
  });

  const toast = useToast();
  const dialog = useAlertDialog();
  const { response } = useClientData();
  const [, setDataFetchingInfo] = useDataFetchIndicator();
  const { fetchELinxSettings, updateELinxSettings } = useELinxSettings();
  const generalSettingsRows = buildGeneralSettings();
  const cardPayments = buildCardPayments(toBool(response.CreditCardEnabled));
  const senderNotifications = buildSenderNotifications();
  const recipientNotifications = buildRecipientNotifications();
  const communications = buildCommunications(defaultValues.DefaultNotificationMethod);
  const { t } = useTranslation('translations', { keyPrefix: 'Elinx.SettingsDialogue.Form' });
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Global.Buttons' });
  const [applyToSubAccounts, setApplyToSubAccounts] = useState(false);
  const [, , , fetchSubAccountsAsync] = useFetchSubAccounts({});

  const isCardPaymentsVisible = toBool(response.CreditCardEnabled) && response.CreditCardProcessor === 'globalpay';

  const fieldDataList: FieldData<ElinxSettingsInfo>[] = [
    ...generalSettingsRows,
    ...(isCardPaymentsVisible ? cardPayments : []),
    ...senderNotifications,
    ...recipientNotifications,
    ...communications,
  ];

  const getElinxSettings = async () => {
    try {
      setDataFetchingInfo(true, '', 'fullScreen');
      const res = await fetchELinxSettings({ PortalUser: response.LoginUserName });

      setDefaultValues({
        TokenExpirationTime: res.ELinxSettings.ELinxExpirationTime,
        IsDeclineEnabled: toBool(res.ELinxSettings.EnableELinxDecline),
        SenderEmailAddress: res.ELinxSettings.ELinxSenderEmailAddress,
        SenderNotifications: toBool(res.ELinxSettings.SenderNotificationsEnabled),
        ReceiverNotifications: toBool(res.ELinxSettings.ReceiverNotificationsEnabled),
        ELinxReminderEnabled: toBool(res.ELinxSettings.ELinxReminderEnabled),
        ELinxReminderFrequency: Number(res.ELinxSettings.ELinxReminderFrequency),
        DefaultNotificationMethod: res.ELinxSettings.DefaultNotificationMethod,
        Trigger3DS: toBool(res.ELinxSettings.Trigger3DS),
        RejectDuplicateRequests: toBool(res.ELinxSettings.RejectDuplicateRequests),
      });
    } catch {
      dialog.caution(t('FetchElinxSettingsDialog.title'), t('FetchElinxSettingsDialog.content'), {
        abortLabel: t('FetchElinxSettingsDialog.abortLabel'),
      });
    } finally {
      setDataFetchingInfo(false);
    }
  };

  const handleSubmit = async (values: ElinxSettingsInfo) => {
    try {
      setDataFetchingInfo(true, '', 'fullScreen');

      const settingsParams = {
        ELinxSenderEmailAddress: values.SenderEmailAddress,
        ELinxExpirationTime: values.TokenExpirationTime,
        EnableELinxDecline: values.IsDeclineEnabled,
        SenderNotificationsEnabled: values.SenderNotifications,
        ReceiverNotificationsEnabled: values.ReceiverNotifications,
        DefaultNotificationMethod: values.DefaultNotificationMethod,
        ELinxReminderEnabled: values.ELinxReminderEnabled,
        ELinxReminderFrequency: values.ELinxReminderFrequency,
        Trigger3DS: values.Trigger3DS,
        RejectDuplicateRequests: values.RejectDuplicateRequests,
      };

      await updateELinxSettings(settingsParams);

      toast.success(t('eLinxSettingsUpdatedToast', { defaultValue: 'eLinx Settings Updated' }));
      getElinxSettings();

      if (applyToSubAccounts) {
        const subAccountsResponse = await fetchSubAccountsAsync({ ShowToken: true, ActiveOnly: true });

        if (!subAccountsResponse.Success) {
          toast.neutral(
            t('eLinxSettingsFetchSubAccountsFailToast', {
              defaultValue: 'eLinx Settings updated, but failed to fetch Sub-Accounts',
            }),
          );
        } else {
          const results = await Promise.allSettled(
            subAccountsResponse.Subaccounts.map((subAccount) => updateELinxSettings(settingsParams, subAccount.Token)),
          );

          const failed = results.filter((r) => r.status === 'rejected').length;

          if (failed > 0) {
            toast.neutral(
              t('eLinxSettingsSubAccountsFailToast', {
                defaultValue: 'eLinx Settings updated, but failed to apply to some Sub-Accounts',
              }),
            );
          } else {
            toast.success(
              t('eLinxSettingsSubAccountsSuccessToast', {
                defaultValue: 'eLinx Settings applied to all Sub-Accounts',
              }),
            );
          }
        }
      }
    } catch {
      toast.neutral(t('eLinxSettingsFailToast'));
    } finally {
      setDataFetchingInfo(false);
    }
  };

  useEffect(() => {
    getElinxSettings();
  }, []);

  const { renderFormElements, ctx } = useForm<ElinxSettingsInfo, ElinxSettingsInfo>({
    config: fieldDataList,
    initialValues: defaultValues,
    onSubmit: async (values) => {
      await handleSubmit(values);

      return onSubmit();
    },
  });

  useEffect(() => {
    ctx.setValues(defaultValues).catch((error) => {
      console.error('Error setting values:', error);
    });
  }, [defaultValues]);

  const isSettingsUpdated = useMemo(() => {
    return !isEqual(ctx.values, defaultValues);
  }, [ctx.values, defaultValues]);

  return (
    <form className={classes('form')} onSubmit={ctx.handleSubmit}>
      <Box flx="col" gapSize="16px">
        <Card title={t('GeneralSettings.title')} style={{ padding: '20px 25px' }}>
          {renderFormElements({
            config: generalSettingsRows,
            override: CustomFormElement,
          })}
        </Card>

        {isCardPaymentsVisible && (
          <Card title={t('CardPayments.title', { defaultValue: 'Card Payments' })} style={{ padding: '20px 25px' }}>
            {renderFormElements({
              config: cardPayments,
              override: CustomFormElement,
            })}
          </Card>
        )}

        <Card title={t('SenderNotifications.title')} style={{ padding: '20px 25px' }}>
          {renderFormElements({
            config: senderNotifications,
            override: CustomFormElement,
          })}
        </Card>

        <Card title={t('RecipientNotifications.title')} style={{ padding: '20px 25px' }}>
          {renderFormElements({
            config: recipientNotifications,
            override: CustomFormElement,
          })}
        </Card>

        <Card title={t('Communications.title')} style={{ padding: '20px 25px' }}>
          {renderFormElements({
            config: communications,
            override: CustomFormElement,
          })}
        </Card>

        {response.subAccountPermissions && (
          <Box ps="sm">
            <Checkbox
              name="apply-to-sub-accounts"
              checked={applyToSubAccounts}
              onChange={(checked) => setApplyToSubAccounts(checked)}
              label={
                <BodyText>
                  {t('addToAllSubAccounts', {
                    defaultValue: 'Apply settings for all existing and future Sub-Accounts',
                  })}
                </BodyText>
              }
            />
          </Box>
        )}

        <Box jc="sb">
          <Button size="standard" onClick={onClose}>
            {tGlobal('cancel')}
          </Button>

          <Button size="standard" disabled={!isSettingsUpdated} colorScheme="primary" type="submit">
            {tGlobal('save')}
          </Button>
        </Box>
      </Box>
    </form>
  );

  function CustomFormElement(fieldData: TemplateData) {
    return (
      <div
        className={classes('optionContainer', fieldData.inputState === 'disabled' && 'disabled')}
        key={fieldData.fieldName}
      >
        <Box ai="center" gapSize={fieldData.inputState === 'required' ? '2px' : '4px'}>
          {/* Toggle rows name their switch input (id `checkbox-${name}`), so tests can find it by label */}
          {isValidElement(fieldData.template) && fieldData.template.type === Toggle ? (
            <label htmlFor={`checkbox-${fieldData.fieldName}`}>{fieldData.label}</label>
          ) : (
            fieldData.label
          )}
          {fieldData.inputState === 'required' && <RequiredIcon />}
          {fieldData.tooltip && fieldData.tooltip}
        </Box>

        <div>{fieldData.template}</div>
      </div>
    );
  }
};

export default ElinxSettingsForm;
